import { type FC } from "react";
import { MenuItem, Stack, Typography } from "@mui/material";
import { ContextMenu } from "@packsize/components";
import { useNavigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

import TwoLineDropdownItem from "@/components/dropdown/two-line-dropdown-item";
import { useReproducePackagingSolution } from "@/hooks/audit";
import { type ClosedCarton } from "@/schemas";

import { useReclosePackagingSolution } from "../../hooks/use-reclose";

interface ClosingHistoryActionMenuProps {
	readonly closedCarton: ClosedCarton;
}

export const ClosingHistoryActionMenu: FC<ClosingHistoryActionMenuProps> = ({ closedCarton }) => {
	const intl = useIntl();
	const navigate = useNavigate();
	const { packagingSolutionId } = closedCarton;

	const { reproduce } = useReproducePackagingSolution(packagingSolutionId);
	const { reclose } = useReclosePackagingSolution(packagingSolutionId);

	return (
		<ContextMenu
			popoverProps={{
				anchorOrigin: {
					vertical: "bottom",
					horizontal: "center",
				},
				transformOrigin: {
					vertical: "top",
					horizontal: "right",
				},
			}}
		>
			<MenuItem onClick={reclose}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<RefreshIcon sx={{ fontSize: 24 }} />
					<TwoLineDropdownItem
						primaryText={intl.formatMessage({ id: "Re-Close", defaultMessage: "Re-Close" })}
						secondaryText={intl.formatMessage({
							id: "Re-induct carton to close again",
							defaultMessage: "Re-induct carton to close again",
						})}
					/>
				</Stack>
			</MenuItem>
			<MenuItem onClick={reproduce}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<ControlPointDuplicateIcon sx={{ fontSize: 24 }} />
					<TwoLineDropdownItem
						primaryText={intl.formatMessage({ id: "Reproduce", defaultMessage: "Reproduce" })}
						secondaryText={intl.formatMessage({ id: "Re-create carton", defaultMessage: "Re-create carton" })}
					/>
				</Stack>
			</MenuItem>
			<MenuItem
				onClick={() => {
					navigate(`/search/${packagingSolutionId}`);
				}}
			>
				<Stack direction="row" alignItems="center" spacing={2}>
					<SearchIcon sx={{ fontSize: 24 }} />
					<Typography variant="body2">
						<FormattedMessage id="See Details" defaultMessage="See Details" />
					</Typography>
				</Stack>
			</MenuItem>
		</ContextMenu>
	);
};
